import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { AUTHOR_PAGE_PERSON_SCHEMA, buildSpeakableSchema } from "@/lib/entitySchema";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";
import { ARTICLES } from "@/lib/learningHub";
import { ArrowRight, BookOpen, Award, MapPin, Mail, Newspaper } from "lucide-react";

const PRESS = [
  {
    name: "Markets Insider",
    url: "https://markets.businessinsider.com/news/stocks/colorado-web-impressions-announces-ai-integration-to-revolutionize-digital-marketing-and-drive-client-success-1033819065",
    label: "AI integration in digital marketing",
  },
  {
    name: "PR Underground",
    url: "https://www.prunderground.com/newsrooms/colorado-web-impressions",
    label: "Newsroom",
  },
  {
    name: "EIN Presswire",
    url: "https://www.einpresswire.com/newsroom/colorado_web_impressions/",
    label: "Newsroom",
  },
  {
    name: "IMDb",
    url: "https://www.imdb.com/name/nm18250804/",
    label: "Author profile",
  },
  {
    name: "Apple Podcasts",
    url: "https://podcasts.apple.com/us/podcast/digital-marketing-coach-with-chris-heidlebaugh/id1875135728",
    label: "Digital Marketing Coach podcast",
  },
];

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/authors/chris-heidlebaugh";
const TITLE = "Chris Heidlebaugh — Author Bio | Colorado Web Impressions";
const DESCRIPTION =
  "Chris Heidlebaugh is the founder of Colorado Web Impressions and author of our SEO, web design, and GEO AI guides. Bio, credentials, and full article archive.";

export const Route = createFileRoute("/authors/chris-heidlebaugh")({
  headers: () => ({
    "Cache-Control": "public, max-age=0, s-maxage=604800, stale-while-revalidate=2592000",
    "CDN-Cache-Control": "public, s-maxage=604800, stale-while-revalidate=2592000",
  }),
  head: () => ({
    meta: [
        { name: "description", content: "Chris Heidlebaugh, owner of Colorado Web Impressions. 25+ years of Colorado SEO, web design, and digital marketing experience. Based in Colorado Springs." },
        { title: "Chris Heidlebaugh — Colorado Springs SEO Expert & Owner" },
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "profile" },
      { property: "og:image", content: `${SITE_URL}/chris-heidlebaugh.png` },
      { property: "profile:first_name", content: "Chris" },
      { property: "profile:last_name", content: "Heidlebaugh" },
      { property: "profile:username", content: "chrisheidlebaugh" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(AUTHOR_PAGE_PERSON_SCHEMA),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildSpeakableSchema(`${SITE_URL}${PATH}`, TITLE)),
      },
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Authors", path: "/about" },
        { name: "Chris Heidlebaugh", path: PATH },
      ]),
    ],
  }),
  component: AuthorPage,
});

function AuthorPage() {
  // Articles authored by Chris (every learn article is currently his).
  const articles = ARTICLES;

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Author"
        title={
          <>
            Chris <span className="text-gradient-gold">Heidlebaugh</span>
          </>
        }
        description="Founder of Colorado Web Impressions. Best-selling author and digital marketing coach with 25+ years helping Colorado small businesses get found, get called, and grow."
      />

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="grid gap-8 md:grid-cols-[200px,1fr] md:items-start">
            <img
              src="/chris-heidlebaugh.png"
              alt="Chris Heidlebaugh, Founder of Colorado Web Impressions"
              className="h-48 w-48 rounded-2xl border border-border bg-card object-cover shadow-card"
              width={192}
              height={192}
            />
            <div className="space-y-4 text-base leading-relaxed text-foreground">
              <p data-speakable>
                Chris Heidlebaugh has spent more than two decades inside the
                Colorado small-business marketing trenches — building websites,
                ranking Google Business Profiles, running paid search, and
                coaching owners through every digital shift from the iPhone era
                to the rise of AI Overviews and generative search.
              </p>
              <p>
                He founded Colorado Web Impressions in 2006 from Colorado
                Springs and still does the work himself: writing the strategy,
                tuning the GBP, editing the copy, and answering the phone. The
                agency stays intentionally small so every client gets owner
                attention.
              </p>
              <p>
                Chris is a best-selling author on digital marketing, a Google
                Ads &amp; Analytics certified practitioner, and a frequent
                speaker on Generative Engine Optimization (GEO) for local
                service businesses.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground">
            Credentials &amp; Recognitions
          </h2>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {[
              { icon: Award, t: "Google Ads Certified" },
              { icon: Award, t: "Google Analytics 4 Certified" },
              { icon: BookOpen, t: "Best-selling digital marketing author" },
              { icon: MapPin, t: "Based in Colorado Springs (since 2006)" },
            ].map((c) => (
              <li
                key={c.t}
                className="flex items-start gap-3 rounded-lg border border-border bg-background p-4 text-sm text-foreground"
              >
                <c.icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-deep" />
                <span>{c.t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <h2 className="flex items-center gap-2 text-2xl font-extrabold text-foreground">
            <Newspaper className="h-6 w-6 text-brand-gold-deep" />
            Featured in
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Independent press, podcast, and author-profile mentions of Chris and Colorado Web Impressions.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PRESS.map((p) => (
              <li key={p.url}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-full flex-col rounded-lg border border-border bg-card p-4 transition-smooth hover:border-brand-gold/60"
                >
                  <span className="text-sm font-bold text-foreground group-hover:text-brand-gold-deep">
                    {p.name}
                  </span>
                  <span className="mt-1 text-xs text-muted-foreground">
                    {p.label}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-section-alt py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground">
            Articles by Chris ({articles.length})
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Every guide, explainer, and pricing breakdown in our learning hub is
            written or edited by Chris personally.
          </p>
          <ul className="mt-6 grid gap-3">
            {articles.map((a) => (
              <li key={a.slug}>
                <Link
                  to={a.slug}
                  className="group flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4 text-sm transition-smooth hover:border-brand-gold/60"
                >
                  <div>
                    <div className="font-semibold text-foreground group-hover:text-brand-gold-deep">
                      {a.title}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {a.category} · {a.read}
                    </div>
                  </div>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-brand-gold-deep" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-background py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-foreground">
            Want Chris on your project?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            Every CWI engagement is led by the founder. No handoffs to junior
            account managers.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg bg-brand-gold-deep px-5 py-3 text-sm font-semibold text-background transition-smooth hover:bg-brand-gold"
            >
              <Mail className="h-4 w-4" />
              Talk to Chris
            </Link>
            <Link
              to="/meet-chris"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60"
            >
              Meet Chris
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
